import { Link } from "react-router-dom";
import { COMPANY } from "../lib/company";
import { EmailLink } from "../components/EmailLink";

// App Review reads this page as the identity check: Meta and Google both look for a
// named, addressable operator behind the app listing. Every fact here is rendered from
// `company.ts` rather than typed in, so the entity name and address can never drift out
// of agreement with the Privacy Policy, the Terms or the footer — a mismatch between
// those is a documented rejection reason.

const PRINCIPLES = [
  {
    tone: "blue",
    glyph: "◇",
    title: "We are a real company, not an app listing",
    body: `${COMPANY.product} is operated by ${COMPANY.legalName}, a company registered in India with a published address, two working phone numbers and a monitored mailbox. You can look us up, write to us and get a person.`,
  },
  {
    tone: "violet",
    glyph: "♢",
    title: "We never hold your social password",
    body: "Connecting an account sends you to Facebook or Google, on their own domain. What returns to us is a revocable, scoped token you can cancel from your platform settings at any moment.",
  },
  {
    tone: "cyan",
    glyph: "▢",
    title: "We say what happened, per destination",
    body: "A post going to three networks keeps three results. One failure never hides two successes, and the provider's own response is shown rather than paraphrased.",
  },
  {
    tone: "green",
    glyph: "♧",
    title: "Your data leaves when you ask it to",
    body: "Deletion is a documented path on this site, not a support ticket. Tokens, media and posts go together.",
  },
];

export function About() {
  return (
    <>
      <section className="about-hero">
        <div className="about-hero-inner">
          <p className="hero-badge"><span aria-hidden="true">✦</span> AI-Assisted. Human-Led.</p>
          <h1>
            Built by people you<br /><span>can actually write to.</span>
          </h1>
          <p className="lede">
            {COMPANY.product} is a social media management platform for businesses and creators.
            Plan, create, schedule and publish from one workspace, with AI-assisted preparation
            and people in control of their brand, voice and publishing decisions.
          </p>
          <p className="about-attribution">
            <strong>{COMPANY.product}</strong> is a product of{" "}
            <strong>{COMPANY.legalName}</strong>. Made with{" "}
            <span className="footer-heart" aria-hidden="true">♥</span>
            <span className="visually-hidden">love</span> using{" "}
            <a href={COMPANY.studio.url} target="_blank" rel="noreferrer noopener">
              {COMPANY.studio.name} &mdash; {COMPANY.studio.tagline}
            </a>.
          </p>
        </div>
      </section>

      <section className="about-principles">
        <div className="band-inner">
          <div className="about-section-head">
            <p className="eyebrow">What we hold ourselves to</p>
            <h2>Four commitments, stated plainly.</h2>
          </div>
          <div className="about-grid">
            {PRINCIPLES.map((p) => (
              <article key={p.title} className={`about-card ${p.tone}`}>
                <i aria-hidden="true">{p.glyph}</i>
                <h3>{p.title}</h3>
                <p>{p.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="about-entity">
        <div className="band-inner">
          <div className="about-entity-grid">
            <div className="about-entity-col">
              <p className="eyebrow">The legal entity</p>
              <h2>Who you are contracting with</h2>
              <p>
                {COMPANY.product} is a trading name. The company behind it, and the party
                to the <Link to="/terms">Terms of Service</Link> and the{" "}
                <Link to="/privacy">Privacy Policy</Link>, is:
              </p>
              <address>
                <strong>{COMPANY.legalName}</strong><br />
                {COMPANY.address.line1}<br />
                {COMPANY.address.line2}<br />
                {COMPANY.address.city}, {COMPANY.address.state} {COMPANY.address.postalCode}<br />
                {COMPANY.address.country}
              </address>
              <p className="muted">
                Governed by the laws of India; courts at {COMPANY.jurisdiction} have
                exclusive jurisdiction.
              </p>
            </div>

            <div className="about-entity-col">
              <p className="eyebrow">How the product is made</p>
              <h2>Made with {COMPANY.studio.name}</h2>
              <p>
                {COMPANY.studio.name} &mdash; {COMPANY.studio.tagline} &mdash; is{" "}
                {COMPANY.legalName}&rsquo;s own AI product builder. It is the toolchain this
                product is designed, written and shipped with, which is why releases land
                quickly and why the same small team can answer for every line of it.
              </p>
              <p>
                It is not an outside vendor and it is not a data processor: no post, token
                or media file you give {COMPANY.product} passes through it. What we do with
                your data is described end to end in the{" "}
                <Link to="/privacy">Privacy Policy</Link>.
              </p>
              <p>
                <a href={COMPANY.studio.url} target="_blank" rel="noreferrer noopener" className="btn-link">
                  Visit {COMPANY.studio.name} <span aria-hidden="true">↗</span>
                </a>
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="about-contact">
        <div className="band-inner">
          <div className="about-section-head">
            <p className="eyebrow">Talk to us</p>
            <h2>One mailbox, two phones, no queue.</h2>
            <p>
              Support, privacy questions and security reports all reach the same monitored
              address. Security reports are answered within two business days.
            </p>
          </div>
          <div className="about-contact-row">
            <div className="about-contact-card">
              <i aria-hidden="true">✉</i>
              <span className="about-contact-label">Email</span>
              <EmailLink address={COMPANY.supportEmail} />
            </div>
            <div className="about-contact-card">
              <i aria-hidden="true">☏</i>
              <span className="about-contact-label">Phone</span>
              <a href={`tel:${COMPANY.phone.replace(/[^+\d]/g, "")}`}>{COMPANY.phone}</a>
            </div>
            <div className="about-contact-card">
              <i aria-hidden="true">⚖</i>
              <span className="about-contact-label">Grievance Officer</span>
              <EmailLink address={COMPANY.grievance.email} />
              <small>
                Acknowledged within {COMPANY.grievance.acknowledgeHours} hours, disposed of
                within {COMPANY.grievance.resolveDays} days, per Rule 3(2)(a).
              </small>
            </div>
          </div>
          <div className="hero-cta about-cta">
            <Link to="/signin" className="btn-link primary lg">Start publishing <span aria-hidden="true">→</span></Link>
            <Link to="/security" className="btn-link lg">Read the security page</Link>
          </div>
        </div>
      </section>
    </>
  );
}
